import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAuth } from '@/lib/authClient';
import { useTrackingActions } from '@/stores/trackingStore';

/**
 * RouterTracker component that integrates with TanStack Router
 * to automatically track page views and handle event flushing
 */
export function RouterTracker() {
  const router = useRouter();
  const { session } = useAuth();
  const { flushEventsBeacon, trackPageView, setPageUrl, setSession } = useTrackingActions();

  useEffect(() => {
    const unsubscribeResolved = router.subscribe('onResolved', ({ toLocation }) => {
      const url = `${toLocation.pathname}${toLocation.searchStr}`;
      setPageUrl(url);
      trackPageView({});
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushEventsBeacon();
      }
    };

    const handleBeforeUnload = () => {
      // Use beacon flush, because fetch is unreliable on unload
      flushEventsBeacon();
    };

    // Use visibilitychange for more reliable detection than beforeunload
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Keep beforeunload as fallback
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      unsubscribeResolved();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [router, flushEventsBeacon, trackPageView, setPageUrl]);

  useEffect(() => {
    setSession(session);
  }, [session, setSession]);

  // This component doesn't render anything
  return null;
}
